import { ACCEPTED_CURRENCY_CODES } from "@/lib/currency/constants";
import {
  isDisplayOnlyCurrency,
  normalizeCurrencyCode,
  shouldShowDualPricing,
} from "@/lib/currency/dualPricingRules";
import { formatPrice } from "@/utils/functions/formatting";

export type FormatDualPriceInput = {
  usdAmount: number;
  displayCurrencyCode: string;
  convertedAmount?: number;
  acceptedCurrencyCodes?: readonly string[];
  dualPricingEnabled?: boolean;
  locale?: string;
};

export type FormatDualPriceResult = {
  mode: "single" | "dual";
  primary: string;
  secondary?: string;
  isDisplayOnly: boolean;
  showTooltip: boolean;
};

const USD_CURRENCY = {
  code: "USD",
  rate: 1,
  symbol: "$",
};

const ZERO_DECIMAL_CURRENCIES = ["JPY", "KRW"];

const getFormatOptions = (currencyCode: string): Intl.NumberFormatOptions =>
  ZERO_DECIMAL_CURRENCIES.includes(currencyCode)
    ? { maximumFractionDigits: 0, minimumFractionDigits: 0 }
    : {};

const ensureCurrencyCodeSuffix = (
  formattedPrice: string,
  currencyCode: string,
): string => {
  const normalizedCode = normalizeCurrencyCode(currencyCode);
  if (formattedPrice.toUpperCase().includes(normalizedCode)) {
    return formattedPrice;
  }

  return `${formattedPrice} ${normalizedCode}`;
};

const assertValidAmount = (amount: number, label: string): void => {
  if (Number.isNaN(amount)) {
    throw Error(`${label} is not a number! amount: ${amount}`);
  }
};

const assertConvertedAmountProvided = (
  displayCurrencyCode: string,
  convertedAmount: number | undefined,
): void => {
  if (displayCurrencyCode !== "USD" && convertedAmount === undefined) {
    throw Error(
      `Converted amount is required when display currency is ${displayCurrencyCode}`,
    );
  }
};

export const formatDualPrice = ({
  usdAmount,
  displayCurrencyCode,
  convertedAmount,
  acceptedCurrencyCodes = ACCEPTED_CURRENCY_CODES,
  dualPricingEnabled = false,
  locale = "en-US",
}: FormatDualPriceInput): FormatDualPriceResult => {
  assertValidAmount(usdAmount, "USD amount");

  const normalizedDisplayCurrency = normalizeCurrencyCode(displayCurrencyCode);
  const isDisplayOnly = isDisplayOnlyCurrency(
    normalizedDisplayCurrency,
    acceptedCurrencyCodes,
  );
  const showDualPricing = shouldShowDualPricing(
    normalizedDisplayCurrency,
    dualPricingEnabled,
    acceptedCurrencyCodes,
  );

  assertConvertedAmountProvided(normalizedDisplayCurrency, convertedAmount);

  const primaryAmount =
    normalizedDisplayCurrency === "USD" ? usdAmount : convertedAmount!;

  if (convertedAmount !== undefined) {
    assertValidAmount(convertedAmount, "Converted amount");
  }

  const primary = ensureCurrencyCodeSuffix(
    formatPrice(
      primaryAmount,
      {
        code: normalizedDisplayCurrency,
        rate: 1,
        symbol: "",
      },
      locale,
      true,
      getFormatOptions(normalizedDisplayCurrency),
    ),
    normalizedDisplayCurrency,
  );

  if (!showDualPricing) {
    return {
      mode: "single",
      primary,
      isDisplayOnly,
      showTooltip: isDisplayOnly,
    };
  }

  const secondary = ensureCurrencyCodeSuffix(
    formatPrice(usdAmount, USD_CURRENCY, locale, true, getFormatOptions("USD")),
    "USD",
  );

  return {
    mode: "dual",
    primary,
    secondary,
    isDisplayOnly,
    showTooltip: isDisplayOnly,
  };
};
